import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Send, CheckCircle, Download, ArrowLeft } from 'lucide-react';
import { trackContactForm } from './AnalyticsProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactForm = ({ formType = 'general', showCompany = false }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    csi_strategy: '',
    csi_alignment: '',
    message: '',
    form_type: formType,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to submit');

      const data = await response.json();
      trackContactForm(formType);
      setSubmittedName(formData.name.split(' ')[0]);
      setSubmitted(true);

      // Reset form data for if they go back
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        csi_strategy: '',
        csi_alignment: '',
        message: '',
        form_type: formType,
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8 space-y-6" data-testid="thank-you-message">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-10 h-10 text-emerald-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-heading font-bold text-slate-900">
            Thank You{submittedName ? `, ${submittedName}` : ''}!
          </h3>
          <p className="text-slate-600 max-w-md mx-auto">
            Your message has been received. Our team will get back to you within 24 hours.
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-100 rounded-2xl p-6 max-w-sm mx-auto">
          <p className="text-sm text-slate-700 mb-4 font-medium">
            Want to learn more about our partnership model?
          </p>
          <a
            href="/MyLifeMatters_Call_For_Partnership.pdf"
            download
            className="inline-flex items-center gap-2 bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all text-sm"
            data-testid="thank-you-download-pdf"
          >
            <Download className="w-4 h-4" />
            Download Partnership Brochure
          </a>
        </div>
        <button
          onClick={() => setSubmitted(false)}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary-mlm transition-colors mt-4"
          data-testid="send-another-message-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`contact-form-${formType}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your full name"
            className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm"
            data-testid="input-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm"
            data-testid="input-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="072 000 0000"
            className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm"
            data-testid="input-phone"
          />
        </div>
        {showCompany && (
          <div className="space-y-2">
            <Label htmlFor="company" className="text-slate-700 font-medium">Company Name</Label>
            <Input
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Your company"
              className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm"
              data-testid="input-company"
            />
          </div>
        )}
      </div>

      {showCompany && (
        <>
          <div className="space-y-2">
            <Label htmlFor="csi_strategy" className="text-slate-700 font-medium">
              How is your organisation currently supporting income-generating opportunities within the gig economy, and is that a priority within your CSI or ESG strategy?
            </Label>
            <Textarea
              id="csi_strategy"
              name="csi_strategy"
              value={formData.csi_strategy}
              onChange={handleChange}
              placeholder="Tell us about your current CSI or ESG strategy..."
              rows={4}
              className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm resize-none"
              data-testid="input-csi-strategy"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="csi_alignment" className="text-slate-700 font-medium">
              If there was a model that could deliver measurable impact through job creation, rider safety, and micro-franchise ownership in last-mile logistics — would that align with your current CSI focus areas?
            </Label>
            <Textarea
              id="csi_alignment"
              name="csi_alignment"
              value={formData.csi_alignment}
              onChange={handleChange}
              placeholder="Share your thoughts on alignment with your CSI focus areas..."
              rows={4}
              className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm resize-none"
              data-testid="input-csi-alignment"
            />
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="message" className="text-slate-700 font-medium">Message *</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          placeholder="How can we help you?"
          rows={5}
          className="border-slate-200 focus:border-primary-mlm focus:ring-primary-mlm resize-none"
          data-testid="input-message"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full py-6 font-semibold shadow-lg hover:shadow-xl transition-all"
        data-testid="submit-contact-form"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
